import {
  PaymentMethod,
  AutoPayEnrollRequest,
  PolicyTypeCd,
} from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBillsQuery } from '../../../api/getBills';
import { useWelcomeQuery } from '../../../api/getWelcomeDetails';
import { useOTSPQuery } from '../../../api/oneTimeScheduledPaymentInfo';
import useGetFeatureFlags from '../../../hooks/useGetFeatureFlags';
import Add from '../../../assets/icons/Add';
import AutoPayOTSPBanner from '../../../components/AutoPayOTSPBanner/AutoPayOTSPBanner';
import ExitConfirmationModal from '../../../components/ExitConfirmationModal';
import WalletSelection from '../../../components/WalletSelection/WalletSelection';
import StyledButton, {
  OutlinedButton,
} from '../../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../../components/styledComponents/Link';
import StatusAlert from '../../../components/styledComponents/StatusAlert';
import {
  AUTOPAY_DISCOUNT_REMOVAL,
  EVALUE_DISCOUNT_REMOVAL,
} from '../../../constants/alertMessages';
import colors from '../../../constants/colors';
import { EValueStatus, StatusType } from '../../../constants/enums';
import { paymentTermsAndConditions } from '../../../constants/paymentMessages';
import useAnalytics from '../../../hooks/useAnalytics';
import usePathnameBlocker from '../../../hooks/usePathnameBlocker';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { usePaymentsContext } from '../../../store/payments';
import { useAutoPayEnrollMutation } from '../utils';

const AutoPayPaymentMethodPage = () => {
  const location = useLocation();
  const isEdit = location.state?.edit;
  const isBulkEnroll = location.state?.bulk;
  const { state } = usePaymentsContext();
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod>('' as PaymentMethod);
  const [selectedPaymentAccountToken, setSelectedPaymentAccountToken] =
    useState('');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const navigate = useNavigate();
  const { closeSnackbar } = useSnackbarsContext();
  const { data: billData } = useBillsQuery();
  const { track } = useAnalytics();

  const isEValueAtRisk =
    !billData?.payments.find((payment) => payment.riskState === 'MD') &&
    [EValueStatus.ACTIVE, EValueStatus.PENDING].includes(
      location.state?.eValue?.toUpperCase() as EValueStatus
    );
  const showEValueDiscountWarning =
    !!selectedPaymentMethod &&
    isEValueAtRisk &&
    selectedPaymentMethod !== 'EFT';

  const { enableNextGen } = useGetFeatureFlags();
  // NEXTGEN-FLAG: on flag retirement, delete `enableNextGen &&` (keep the dataSource check)
  const isNextGenPolicy =
    enableNextGen && state.selectedBill.dataSource === 'PLPC';

  const isUsingAchForAutopay =
    !!state.selectedBill.autoPay?.enrolled &&
    !!state.selectedBill.autoPay?.account;
  const showNextGenAutopayWarning =
    isNextGenPolicy && isUsingAchForAutopay && selectedPaymentMethod === 'CRDC';

  const showEValueWarning =
    showEValueDiscountWarning || showNextGenAutopayWarning;

  const warningMessage = isNextGenPolicy
    ? AUTOPAY_DISCOUNT_REMOVAL
    : EVALUE_DISCOUNT_REMOVAL;

  useEffect(() => {
    if (showEValueWarning) {
      track('User Messaged', {
        location: 'Feature Card',
        event_type: 'Messaged',
        message_type: 'Warning',
        contents: warningMessage,
        policy_number: state.selectedBill.policyNumber,
      });
    }
  }, [showEValueWarning]);

  const { enroll } = useAutoPayEnrollMutation(isBulkEnroll, isEdit);

  const { data: welcomeData } = useWelcomeQuery();
  const { data: otspData } = useOTSPQuery(welcomeData?.policies);
  const scheduledPayment =
    otspData?.[state.selectedBill.policyNumber]?.schedulePaymentDetails; // OTSP for the current policy

  const buttonText = (() => {
    if (showEValueWarning) return 'Continue and remove discount';
    if (isEdit) return 'Save';

    return 'Finish';
  })();

  const handleClick = async () => {
    if (showEValueWarning) {
      track('User Status Changed', {
        location: 'Feature Card',
        event_type: 'Automated System Process',
        evalue_status: 'lost',
        status_changed: 'evalue',
        policy_number: state.selectedBill.policyNumber,
        state: state.selectedBill.riskState,
      });
    }

    let policies: AutoPayEnrollRequest[] = [];

    if (isBulkEnroll) {
      track('AutoPay Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        setup_type: 'all policies',
        account_type: state.selectedPaymentMethod?.analyticsAccountType,
        status: 'On',
      });

      policies =
        billData?.payments
          ?.filter(
            (payment) => payment.autoPay.eligible && !payment.autoPay.enrolled
          )
          ?.map((payment) => ({
            policyNumber: payment.policyNumber,
            policyInfo: {
              productType: payment.policyTypeCd as PolicyTypeCd,
              dataSource: payment.dataSource,
            },
            paymentItem: {
              paymentMethod: selectedPaymentMethod,
              paymentAccountToken: selectedPaymentAccountToken,
            },
          })) ?? [];
    } else {
      const { selectedBill } = state;

      track('AutoPay Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        setup_type: isBulkEnroll ? 'all policies' : 'single policy',
        policy_number: selectedBill.policyNumber,
        account_type: state.selectedPaymentMethod?.analyticsAccountType,
        state: selectedBill.riskState,
        policy_type: selectedBill.policyType,
        status: 'On',
      });

      policies = [
        {
          policyNumber: selectedBill.policyNumber,
          policyInfo: {
            productType: selectedBill.policyTypeCd as PolicyTypeCd,
            dataSource: selectedBill.dataSource,
          },
          paymentItem: {
            paymentMethod: selectedPaymentMethod,
            paymentAccountToken: selectedPaymentAccountToken,
          },
        },
      ];
    }

    await enroll.mutateAsync({
      policies,
    });
  };

  const navigationBlocker = usePathnameBlocker(
    ['/logout', '/autopay'],
    setShowExitDialog
  );

  return (
    <>
      <ExitConfirmationModal
        open={showExitDialog}
        onClose={() => {
          setShowExitDialog(false);
        }}
        handleContinue={async () => {
          navigationBlocker();
        }}
        title="Are you sure you want to leave this page?"
        bodyText={[
          'By selecting "Yes", your AutoPay enrollment will not be completed',
        ]}
        leaveText="Yes, leave"
        stayText="No, stay"
        showRemoveIcon
      />
      <Stack
        direction="column"
        data-testid="paymentMethodPage"
        sx={{
          width: 1,
          minHeight: '100%',
          backgroundColor: colors.csaaGray,
          px: { mobile: 2, desktop: 19.5 },
          pb: 3,
          pt: { mobile: 2, tablet: 3, desktop: 9 },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: { mobile: 400, tablet: 500 },
            fontSize: { mobile: 33, tablet: 44 },
            pb: { mobile: 0.5, tablet: 1 },
            lineHeight: { mobile: '32px', tablet: '56px' },
          }}
        >
          How would you like to pay?
        </Typography>
        <Card
          component={Stack}
          sx={{
            borderRadius: 2.5,
            mt: 3,
            px: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            py: {
              mobile: 2,
              tablet: 3,
              desktop: 5,
            },
            height: 'auto',
            boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 28 }}>
            Payment method
          </Typography>
          {scheduledPayment?.paymentAmount && scheduledPayment?.paymentDate && (
            <AutoPayOTSPBanner
              paymentAmount={scheduledPayment?.paymentAmount}
              scheduledDate={scheduledPayment?.paymentDate}
            />
          )}
          {showEValueWarning && (
            <Box sx={{ mt: 3 }}>
              <StatusAlert
                text={
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '18px',
                      fontFamily: 'Cabin',
                    }}
                  >
                    {warningMessage}
                  </Typography>
                }
                open={showEValueWarning}
                status={StatusType.WARNING}
              />
            </Box>
          )}
          <Stack direction="column">
            <Stack
              direction="row"
              sx={{
                justifyContent: { mobile: 'center', tablet: 'flex-end' },
                mt: 2,
              }}
            >
              <Button
                startIcon={<Add />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 18,
                }}
                onClick={() => {
                  track('Payment Methods Accessed', {
                    event_type: 'Link Accessed',
                    location: 'Feature Card',
                    link: 'Add payment method',
                    policy_number: state.selectedBill.policyNumber,
                    state: state.selectedBill.riskState,
                  });
                  navigate('add', { state: location.state });
                }}
                disableRipple
              >
                Add payment method
              </Button>
            </Stack>
          </Stack>
          <WalletSelection
            setSelectedPaymentType={setSelectedPaymentMethod}
            setSelectedPaymentAccountToken={setSelectedPaymentAccountToken}
            selectedPaymentAccountToken={selectedPaymentAccountToken}
          />
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            spacing={{ mobile: 1, tablet: 2 }}
            sx={{
              justifyContent: 'flex-end',
              mt: 3,
            }}
          >
            <OutlinedButton
              width="auto"
              data-testid="paymentMethodSelectCancel"
              sx={{
                borderWidth: 2,
              }}
              onClick={() => {
                closeSnackbar();
                navigate('/autopay');
              }}
            >
              Cancel
            </OutlinedButton>
            <StyledButton
              data-testid="paymentMethodSelectContinue"
              disabled={!selectedPaymentAccountToken || enroll.isPending}
              onClick={handleClick}
              loading={enroll.isPending}
            >
              {buttonText}
            </StyledButton>
          </Stack>
          <Stack
            sx={{
              alignItems: 'flex-end',
              pt: { mobile: 1, tablet: 3 },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: 12,
                lineHeight: '16px',
                textAlign: { mobile: 'center', tablet: 'right' },
                letterSpacing: '0.2px',
              }}
            >
              {paymentTermsAndConditions(buttonText)}
              <LinkWithNoRouter
                href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/auth-terms.html"
                onClick={() =>
                  track('Terms and Conditions Accessed', {
                    event_type: 'Link Accessed',
                    link: 'Terms of Use',
                    location: 'Feature Card',
                    policy_number: state.selectedBill.policyNumber,
                  })
                }
                target="_blank"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                AutoPay Terms and Conditions
              </LinkWithNoRouter>
              .
            </Typography>
          </Stack>
        </Card>
      </Stack>
    </>
  );
};

export default AutoPayPaymentMethodPage;
